import {
  FanguardApiContractsClientVenueDetailModel,
  FanguardApiContractsClientVenueRequestModel,
  FanguardApiContractsClientVenueSummaryModel,
} from 'src/api/generated';
import { get, patch, post, remove } from '@/api/api';
import { useQuery } from '@tanstack/react-query';
import { useMutationWithMessages } from './helpers';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import queryClient from 'src/queryClient';

const URI = 'venues';

const useGetVenues = () => {
  const query = useQuery({
    queryKey: [URI],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientVenueSummaryModel[]>({ uri: URI });

      return response.data;
    },
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (query.isError) {
      queryClient.removeQueries({ queryKey: [URI] });
    }
  }, [query.isError]);

  return query;
};

const useGetVenue = (id?: string) => {
  return useQuery({
    queryKey: [URI, id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientVenueDetailModel>({ uri: `${URI}/${id}` });
      return response.data;
    },
    enabled: !!id,
  });
};

const useCreateVenue = () => {
  return useMutationWithMessages((data: FanguardApiContractsClientVenueRequestModel) => post({ uri: URI, data }), {
    entityType: 'venue',
    action: 'create',
    queryKeysToInvalidate: [URI],
  });
};

const useUpdateVenue = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${URI}/${data.id}`, data }),
    {
      entityType: 'venue',
      action: 'update',
      queryKeysToInvalidate: [URI],
    },
  );
};

const useUploadVenueImage = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${URI}/${data.id}`, data }),
    {
      entityType: 'image',
      action: 'upload',
      queryKeysToInvalidate: [URI],
    },
  );
};

const useDeleteVenueImage = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${URI}/${data.id}`, data }),
    {
      entityType: 'image',
      action: 'delete',
      queryKeysToInvalidate: [URI],
    },
  );
};

const useDeleteVenue = () => {
  return useMutationWithMessages((venueId: string) => remove({ uri: `${URI}/${venueId}` }), {
    entityType: 'venue',
    action: 'delete',
    queryKeysToInvalidate: [URI],
  });
};

export {
  useGetVenues,
  useGetVenue,
  useCreateVenue,
  useUpdateVenue,
  useDeleteVenue,
  useUploadVenueImage,
  useDeleteVenueImage,
};
